export class DateTimeStringUtility {
  /**
   * 値が空であることを意味するマジックナンバーか。
   * マジックナンバー:
   * "00000000"(8桁すべてが0), "99999999"(8桁すべてが9), 
   * "000000000000"(12桁すべてが0), "999999999999"(12桁すべてが9),
   * "00000000000000"(14桁すべてが0), "99999999999999"(14桁すべてが9)
   */
  static isEmptyDate(text: string): boolean {
    return (/^(0{8}|0{12}|0{14}|9{8}|9{12}|9{14})$/).test(text);
  }

  /** 与えられた文字列がYYYYMMDD形式(8桁かつ数値のみ)を満たすかどうか。 */
  static isDateFormat(text: string): boolean {
    return (/^[0-9]{8}$/).test(text);
  }

  /**
   * 与えられた文字列がHHmm[ss]形式(4桁または6桁であり、数値のみ)を満たすかどうか。 
   * [ss]は省略可能。
   */
  static isTimeFormat(text: string): boolean {
    return (/^([0-9]{4}|[0-9]{6})$/).test(text);
  }

  /**
   * 与えられた文字列がYYYYMMDDHHmm[ss]形式(12桁または14桁であり、数値のみ)を満たすかどうか。 
   * [ss]は省略可能。
   */
  static isDateTimeFormat(text: string): boolean {
    return (/^([0-9]{12}|[0-9]{14})$/).test(text);
  }
  
  /** YYYYMMDD形式の文字列をDate型に変換する。 */
  static toDateValue(YYYYMMDD: string): Date {
    if (!this.isDateFormat(YYYYMMDD)) {
      throw new Error("argument error: " + YYYYMMDD + " はYYYYMMDD形式ではありません。");
    }
    const YYYY = Number(YYYYMMDD.substr(0, 4));
    const MM = Number(YYYYMMDD.substr(4, 2));
    const DD = Number(YYYYMMDD.substr(6, 2));
    return new Date(YYYY, MM - 1, DD);
  }

  /**
   * HHmm[ss]形式の文字列をDate型に変換する。
   * [ss]を省略したときは"00"が設定される。
   */
  static toTimeValue(HHmmss: string): Date {
    if (!this.isTimeFormat(HHmmss)) {
      throw new Error("argument error: " + HHmmss + " はHHmm[ss]形式ではありません。");
    }

    const padding = (HHmmss + "00").slice(0, 6);
    const HH = Number(padding.substr(0, 2));
    const mm = Number(padding.substr(2, 2));
    const ss = Number(padding.substr(4, 2));
    return new Date(1970, 0, 1, HH, mm, ss); // 年月日は使用しないので1970/1/1
  }

  /**
   * YYYYMMDDHHmm[ss]形式の文字列をDate型に変換する。
   * [ss]を省略したときは"00"が設定される。
   */
  static toDateTimeValue(YYYYMMDDHHmmss: string): Date {
    if (!this.isDateTimeFormat(YYYYMMDDHHmmss)) {
      throw new Error("argument error: " + YYYYMMDDHHmmss + " はYYYYMMDDHHmm[ss]形式ではありません。");
    }

    const padding = (YYYYMMDDHHmmss + "00").slice(0, 14);
    const YYYY = Number(padding.substr(0, 4));
    const MM = Number(padding.substr(4, 2));
    const DD = Number(padding.substr(6, 2));
    const HH = Number(padding.substr(8, 2));
    const mm = Number(padding.substr(10, 2));
    const ss = Number(padding.substr(12, 2));
    return new Date(YYYY, MM - 1, DD, HH, mm, ss);
  }

  static convertToDateTime(date: Date, time: Date): Date {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes(),
      time.getSeconds()
    );
  }

  static fromDateTimeValue(dateTime: Date): string {
    return this.fromDateValue(dateTime)
      + this.fromTimeValue(dateTime);
  }

  static fromDateValue(date: Date): string {
    return date.getFullYear()
      + ("0" + (date.getMonth() + 1)).slice(-2)
      + ("0" + date.getDate()).slice(-2);
  }

  static fromTimeValue(time: Date): string {
    return ("0" + time.getHours()).slice(-2)
      + ("0" + time.getMinutes()).slice(-2)
      + ("0" + time.getSeconds()).slice(-2);
  }
}
